import { useRef, useState } from "react";
import IconStarFill from "@/components/icons/icon-star-fill";

export interface TestimonialCardProps {
	/** The testimonial text content */
	content: string;
	/** Author's name */
	authorName: string;
	/** Author's title/role (e.g., "Founder of Superblog") */
	authorTitle?: string;
	/** URL to the author's avatar image */
	avatarUrl?: string;
	/** Rating out of 5 stars */
	rating?: 1 | 2 | 3 | 4 | 5;
	/** Optional highlighted text within the content */
	highlightedText?: string;
	/** Color for highlighted text */
	highlightColor?: string;
	/** Accent color for the card border and decorative elements */
	accentColor?: string;
	/** Background color of the card */
	backgroundColor?: string;
	/** Text color for the card content */
	textColor?: string;
	/** Whether to show the star rating */
	showRating?: boolean;
	/** Whether to show the author avatar */
	showAvatar?: boolean;
	/** Custom color for rating stars */
	ratingStarColor?: string;
	/** Thumbnail URL for video testimonials */
	videoThumbnailUrl?: string;
	/** Callback when video play button is clicked */
	onVideoPlay?: () => void;
	/** Audio URL for audio testimonials */
	audioUrl?: string;
	/** Pre-calculated audio duration from the API */
	audioDuration?: number | null;
	/** Optional className for additional styling */
	className?: string;
}

function AudioPlayer({ src, initialDuration }: { src: string; initialDuration?: number | null }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);

	const formatTime = (time: number) => {
		if (isNaN(time) || !isFinite(time)) return "0:00";
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	const togglePlay = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleTimeUpdate = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			setCurrentTime(audioRef.current.currentTime);
			if (!duration) {
				setDuration(audioDur);
			}
		}
	};

	const handleLoadedMetadata = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0 && !initialDuration) {
			setDuration(audioDur);
		}
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const time = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = time;
			setCurrentTime(time);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
		setCurrentTime(0);
	};

	const progress = duration > 0 && isFinite(duration) ? Math.min((currentTime / duration) * 100, 100) : 0;

	return (
		<div className="mb-4 p-3 rounded-xl bg-gray-100 max-w-[480px] mx-auto">
			<audio ref={audioRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />
			<div className="flex items-center gap-3">
				<button type="button" onClick={togglePlay} className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 shadow-sm text-black bg-white">
					{isPlaying ? (
						<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
						</svg>
					) : (
						<svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
					)}
				</button>
				<div className="flex-1 flex items-center gap-2">
					<span className="text-xs tabular-nums text-gray-500">{formatTime(currentTime)}</span>
					<div className="flex-1 relative h-1.5 rounded-full overflow-hidden bg-gray-300/50">
						<div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: "#1f1f1f" }} />
						<input type="range" min={0} max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
					</div>
					{duration >= 1 && <span className="text-xs tabular-nums text-gray-500">{formatTime(duration)}</span>}
				</div>
			</div>
		</div>
	);
}

export default function TestimonialCard({
	content,
	authorName,
	authorTitle,
	avatarUrl,
	rating = 5,
	highlightedText,
	highlightColor = "#6366f1",
	accentColor = "#6366f1",
	backgroundColor,
	textColor,
	showRating = true,
	showAvatar = true,
	ratingStarColor,
	videoThumbnailUrl,
	onVideoPlay,
	audioUrl,
	audioDuration,
	className = "",
}: TestimonialCardProps) {
	// Function to render content with highlighted text
	const renderContent = () => {
		if (!highlightedText) {
			return content;
		}

		const parts = content.split(new RegExp(`(${highlightedText})`, "gi"));
		return parts.map((part, index) =>
			part.toLowerCase() === highlightedText.toLowerCase() ? (
				<span key={index} style={{ color: highlightColor }} className="font-semibold">
					{part}
				</span>
			) : (
				part
			),
		);
	};

	// Get initials for fallback avatar
	const getInitials = (name: string) => {
		return name
			.split(" ")
			.map((word) => word[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	return (
		<div className={`w-full ${className}`} style={backgroundColor ? { backgroundColor } : undefined}>
			<div className="p-6 text-center">
				{/* Header: Author */}
				<div className="flex flex-col items-center gap-2 mb-3">
					{/* Avatar */}
					{showAvatar &&
						(avatarUrl ? (
							<img src={avatarUrl} alt={authorName} className="w-11 h-11 rounded-lg object-cover" />
						) : (
							<div className="w-11 h-11 rounded-lg flex items-center justify-center text-white font-semibold text-sm" style={{ backgroundColor: accentColor }}>
								{getInitials(authorName)}
							</div>
						))}

					{/* Author Details */}
					<div>
						<p className={`font-semibold text-base ${textColor ? "" : "text-gray-900"}`} style={textColor ? { color: textColor } : undefined}>
							{authorName}
						</p>
						{authorTitle && (
							<p className={`text-sm ${textColor ? "" : "text-gray-500"}`} style={textColor ? { color: textColor, opacity: 0.6 } : undefined}>
								{authorTitle}
							</p>
						)}
					</div>
				</div>

				{/* Star Rating */}
				{showRating && rating && (
					<div className="flex justify-center gap-0.5 mb-3">
						{[...Array(rating)].map((_, index) => (
							<IconStarFill key={index} className={`w-7 h-7 ${ratingStarColor ? "" : "text-amber-400"}`} style={ratingStarColor ? { color: ratingStarColor } : undefined} />
						))}
					</div>
				)}

				{/* Audio Player */}
				{audioUrl && <AudioPlayer src={audioUrl} initialDuration={audioDuration} />}

				{/* Video Thumbnail */}
				{videoThumbnailUrl && (
					<button type="button" onClick={() => onVideoPlay?.()} className="w-full overflow-hidden rounded-xl relative block cursor-pointer mb-4 mx-auto max-w-[480px]">
						<img src={videoThumbnailUrl} alt="" className="w-full h-48 object-cover" />
						<div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
							<div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: "#1f1f1f" }}>
								<svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
									<path d="M8 5v14l11-7z" />
								</svg>
							</div>
						</div>
					</button>
				)}

				{/* Testimonial Content */}
				<blockquote className="max-w-[480px] mx-auto">
					<p className={`text-lg leading-relaxed ${textColor ? "" : "text-gray-600"}`} style={textColor ? { color: textColor, opacity: 0.8 } : undefined}>
						"{renderContent()}"
					</p>
				</blockquote>
			</div>
		</div>
	);
}
